import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Camera, CheckCircle, ClipboardCheck, Users, Thermometer, BookOpen, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const PublicLandingPage = () => {
  // Product descriptions - consistent with worksafeagent.com
  const products = [
    {
      name: 'WSafe AI',
      description: 'AI hazard detection on existing cameras and phones, reviewed by a competent person',
      icon: Camera,
      color: '#17475F'
    },
    {
      name: 'HazardLoop',
      description: 'Corrective action tracking and verified closeout',
      icon: CheckCircle,
      color: '#F28C1E'
    },
    {
      name: 'InductIQ',
      description: 'Induction/training verification and incident reporting',
      icon: Users,
      color: '#17475F'
    },
    {
      name: 'SafeForm',
      description: 'Ergonomic analysis',
      icon: ClipboardCheck,
      color: '#F28C1E'
    },
    {
      name: 'EnviSafe',
      description: 'Environmental conditions monitoring',
      icon: Thermometer,
      color: '#17475F'
    },
    {
      name: 'WorkSafeStory',
      description: 'Safety stories and lessons learned',
      icon: BookOpen,
      color: '#F28C1E'
    }
  ];

  // Pricing tiers - matching worksafeagent.com
  const tiers = [
    {
      name: 'Core',
      price: '$1,500',
      period: '/site/month',
      setup: '$5,000 setup',
      includes: ['WSafe AI', 'HazardLoop', 'WorkSafeStory'],
      highlight: false
    },
    {
      name: 'Pro',
      price: '$3,000',
      period: '/site/month',
      setup: '$10,000 setup',
      includes: ['Everything in Core', 'InductIQ', 'EnviSafe'],
      highlight: true
    },
    {
      name: 'Vision',
      price: '$6,250',
      period: '/site/month',
      setup: '$25,000 setup',
      includes: ['Everything in Pro', 'SafeForm', 'Continuous CCTV detection'],
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{fontFamily: "'Inter', sans-serif"}}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center">
            <img 
              src="https://wsafeai.github.io/wsafeai-procore-prototype/brand/products/wsafe-ai/wsafe-ai-lockup-light.svg" 
              alt="WSafe AI" 
              className="h-8"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{backgroundColor: '#17475F'}}>
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold" style={{color: '#17475F'}}>WSafe AI</span>
            </div>
          </a>
          <div className="flex items-center gap-4">
            <a 
              href="https://worksafeagent.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium hidden sm:inline-flex items-center gap-1"
            >
              WorkSafeAgent
              <ExternalLink className="w-3 h-3" />
            </a>
            <Link to="/login">
              <Button 
                className="text-white font-medium rounded-xl px-6"
                style={{backgroundColor: '#F28C1E'}}
              >
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{color: '#17475F'}} data-testid="hero-headline">
            Your cameras watch for hazards so your competent person can spend the day fixing them, not hunting for them.
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Every AI alert is reviewed by a competent person before any action is taken.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button 
                size="lg"
                className="text-white font-semibold rounded-xl px-8 h-12"
                style={{backgroundColor: '#F28C1E'}}
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="https://worksafeagent.com" target="_blank" rel="noopener noreferrer">
              <Button 
                variant="outline"
                size="lg"
                className="font-semibold rounded-xl px-8 h-12 border-2"
                style={{borderColor: '#17475F', color: '#17475F'}}
              >
                Learn More
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4" style={{color: '#17475F'}}>
            Our Products
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            A complete suite of safety tools designed to work together
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product, index) => (
              <Card key={index} className="bg-white border border-gray-200 rounded-2xl hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{backgroundColor: `${product.color}15`}}
                  >
                    <product.icon className="w-6 h-6" style={{color: product.color}} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{color: '#17475F'}}>
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {product.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4" style={{color: '#17475F'}}>
            Simple Pricing
          </h2>
          <p className="text-gray-600 text-center mb-12">
            Choose the plan that fits your operation.{' '}
            <a 
              href="https://worksafeagent.com/pricing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{color: '#F28C1E'}}
            >
              See full details →
            </a>
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`rounded-2xl overflow-hidden ${
                  tier.highlight 
                    ? 'border-2 shadow-lg scale-105' 
                    : 'border border-gray-200'
                }`}
                style={tier.highlight ? {borderColor: '#F28C1E'} : {}}
              >
                {tier.highlight && (
                  <div 
                    className="text-white text-center py-2 text-sm font-medium"
                    style={{backgroundColor: '#F28C1E'}}
                  >
                    Most Popular
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2" style={{color: '#17475F'}}>
                    {tier.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold" style={{color: '#17475F'}}>{tier.price}</span>
                    <span className="text-gray-500 text-sm">{tier.period}</span>
                    <p className="text-gray-500 text-sm mt-1">+ {tier.setup}</p>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {tier.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{color: '#F28C1E'}} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="https://worksafeagent.com/pricing" target="_blank" rel="noopener noreferrer">
                    <Button 
                      className={`w-full rounded-xl font-medium ${
                        tier.highlight ? 'text-white' : ''
                      }`}
                      variant={tier.highlight ? 'default' : 'outline'}
                      style={tier.highlight 
                        ? {backgroundColor: '#F28C1E'} 
                        : {borderColor: '#17475F', color: '#17475F'}
                      }
                    >
                      View Details
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Founding Pilot */}
          <div className="mt-12 text-center p-8 rounded-2xl" style={{backgroundColor: '#FFF8F0', border: '1px solid #F28C1E30'}}>
            <h3 className="text-xl font-bold mb-2" style={{color: '#17475F'}}>
              Founding Pilot Program
            </h3>
            <p className="text-gray-600 mb-4">
              $1,000 one-time, no setup fee. Limited spots available.
            </p>
            <a href="mailto:pilot@worksafeagent.com?subject=Founding%20Pilot%20Interest">
              <Button 
                className="text-white font-medium rounded-xl px-8"
                style={{backgroundColor: '#F28C1E'}}
              >
                Apply for Pilot
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6" style={{backgroundColor: '#17475F'}}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to improve your safety program?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Start detecting hazards with AI today. Every alert reviewed by qualified personnel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button 
                size="lg"
                className="font-semibold rounded-xl px-8 h-12"
                style={{backgroundColor: '#F28C1E', color: 'white'}}
              >
                Sign in
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="https://worksafeagent.com" target="_blank" rel="noopener noreferrer">
              <Button 
                variant="outline"
                size="lg"
                className="font-semibold rounded-xl px-8 h-12 bg-transparent border-white text-white hover:bg-white/10"
              >
                Visit WorkSafeAgent
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="https://wsafeai.github.io/wsafeai-procore-prototype/brand/products/wsafe-ai/wsafe-ai-lockup-light.svg" 
                alt="WSafe AI" 
                className="h-6"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className="text-gray-500 text-sm">© 2026 WorkSafeAgent LLC</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-700">
                Privacy
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-700">
                Terms
              </Link>
              <a 
                href="https://worksafeagent.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
              >
                WorkSafeAgent
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <p className="text-gray-400 text-xs">
              USPTO Provisional Patent 63/992,261 · Published technical paper (Zenodo): DOI 10.5281/zenodo.20499515
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLandingPage;
